<script lang="ts">
  import { Route, router } from 'tinro'
  import { onMount } from 'svelte'
  import LoginPage from './pages/LoginPage.svelte'
  import LazyPage, { prefetchPage } from './components/LazyPage.svelte'
  import { prefetchAllowed, runWhenIdle, whenLoaded } from './utils/prefetch.js'
  import { adminPortalV2, audioStudio, flagsKnown, localFlag } from './utils/featureFlags.js'
  import { getProfile } from './utils/ApiCalls.svelte'
  import { getCookie } from './utils/CookieUtils.svelte'
  import {
    currentPage,
    loginToken,
    leftMenuToggled,
    userConfig,
  } from './components/DataStore'

  // pages load on demand (LazyPage); only the login page is part of the first download
  const pages = {
    IndexPage: () => import('./pages/IndexPage.svelte'),
    RegistrationPage: () => import('./pages/RegistrationPage.svelte'),
    ProfilePage: () => import('./pages/ProfilePage.svelte'),
    StudentPage: () => import('./pages/StudentPage.svelte'),
    StudentDetailPage: () => import('./pages/StudentDetailPage.svelte'),
    StudentRecordingPage: () => import('./pages/StudentRecordingPage.svelte'),
    AudioStudioPage: () => import('./pages/AudioStudio/AudioStudioPage.svelte'),
    AdminPortalPage: () => import('./pages/AdminPortal/AdminPortalPage.svelte'),
    AllRecordingsPage: () => import('./pages/AllRecordings/AllRecordingsPage.svelte'),
    AllRecordingsSummaryPage: () => import('./pages/AllRecordings/AllRecordingsSummaryPage.svelte'),
    CoursePage: () => import('./pages/Course/CoursePage.svelte'),
    CourseDetailPage: () => import('./pages/Course/CourseDetailPage.svelte'),
    CourseDetailEditPage: () => import('./pages/Course/CourseDetailEditPage.svelte'),
    LessonDetailPage: () => import('./pages/Lesson/LessonDetailPage.svelte'),
    LessonDetailEditPage: () => import('./pages/Lesson/LessonDetailEditPage.svelte'),
    ExerciseDetailPage: () => import('./pages/Exercise/ExerciseDetailPage.svelte'),
    ExerciseDetailEditPage: () => import('./pages/Exercise/ExerciseDetailEditPage.svelte'),
    RecordingDetailPage: () => import('./pages/Recording/RecordingDetailPage.svelte'),
    ResetPasswordPage: () => import('./pages/ResetPasswordPage.svelte'),
    ResetPasswordVerifyPage: () => import('./pages/ResetPasswordVerifyPage.svelte'),
    SearchResultPage: () => import('./pages/SearchResultPage.svelte'),
    ContentMarketPlaceCallbackPage: () => import('./pages/ContentMarketPlaceCallbackPage.svelte'),
    ActivityLogPage: () => import('./pages/ActivityLogPage.svelte'),
    OrganizationManagement: () => import('./pages/OrganizationManagement.svelte'),
    OneCampusPage: () => import('./pages/OneCampusPage.svelte'),
    SchoolPlusPage: () => import('./pages/SchoolPlusPage.svelte'),
    RegistrationFromInvitePage: () => import('./pages/RegistrationFromInvitePage.svelte'),
    StudentAnalyticsPage: () => import('./pages/StudentAnalyticsPage.svelte'),
    StudentRosterEditPage: () => import('./pages/StudentRosterEditPage.svelte'),
    StudentRosterPage: () => import('./pages/StudentRosterPage.svelte'),
    StudentRosterDetailPage: () => import('./pages/StudentRosterDetailPage.svelte'),
    OneCampusSyncWizardPage: () => import('./pages/OneCampusSyncWizardPage.svelte'),
    StudentAccountDetailPage: () => import('./pages/StudentAccountDetailPage.svelte'),
    TeacherDetailPage: () => import('./pages/TeacherDetailPage.svelte'),
    StudentsInOrgPage: () => import('./pages/StudentsInOrgPage.svelte'),
    ImportStudentsToOrgPage: () => import('./pages/ImportStudentsToOrgPage.svelte'),
    RequestQuotePage: () => import('./pages/RequestQuotePage.svelte'),
    SpecialEventPage: () => import('./pages/SpecialEvent/SpecialEventPage.svelte'),
    PracticePacksPage: () => import('./pages/PracticePacks/PracticePacksPage.svelte'),
  }
  type PageName = keyof typeof pages

  // pages anyone can open; all the others are for signed-in users
  const PUBLIC_PAGES: PageName[] = [
    'RegistrationPage',
    'OneCampusPage',
    'SchoolPlusPage',
    'RegistrationFromInvitePage',
    'ResetPasswordPage',
    'ResetPasswordVerifyPage',
  ]

  // The first visit to a page waits for its code, showing an empty screen meanwhile. To spare that, the code is
  // fetched ahead of time once the current page has loaded and the browser is idle: on the login page the
  // dashboard's (it comes next), once signed in every page's.
  function prefetch(names: PageName[]) {
    if (!prefetchAllowed()) return
    whenLoaded().then(() => {
      performance.mark?.('prefetch-start') // where scripts/measure-browser.mjs counts background downloads from
      return runWhenIdle(names.map((name) => () => prefetchPage(pages[name])))
    })
  }
  let prefetchedSignedIn = false
  $: if ($loginToken && !prefetchedSignedIn) {
    prefetchedSignedIn = true
    prefetch((Object.keys(pages) as PageName[]).filter((name) => !PUBLIC_PAGES.includes(name)))
  }

  router.mode.hash()

  let page

  // translations are set up before the app is mounted (src/i18n/index.js)

  onMount(async () => {
    const url_string = window.location.href
    if (url_string.match('reset-password-verify')) {
      return
    }
    if (url_string.match('reset-password')) {
      return
    }
    if (url_string.match('registration')) {
      return
    }

    if (url_string.match('one-campus')) {
      let urlParams = new URLSearchParams(document.location.search)
      page = 'one-campus'
      if (document.location.search.match('one-campus') || urlParams.has('dsns') || urlParams.has('code')) {
        // The link from 1Campus (/?one-campus&dsns=…&code=…&role=…). Its code works once: it leaves the address
        // without a reload (a reload used to sign in again with no code, so every sign-in ended on the error page),
        // and the sign-in page replaces the entry, so neither a later reload nor Back sends it again.
        history.replaceState(history.state, '', location.pathname + location.hash)
        const query = ['dsns', 'code', 'role']
          .filter((name) => urlParams.get(name))
          .map((name) => `${name}=${urlParams.get(name)}`)
          .join('&')
        router.goto(`/one-campus${query ? '?' + query : ''}`, true)
      }
      // otherwise the sign-in page was reloaded and its parameters are in the route already
      return
    }

    if (url_string.match('schoolplus')) {
      let urlParams = new URLSearchParams(document.location.search)
      //document.location.search = ''
      page = 'schoolplus'
      router.goto(
        `/schoolplus?&code=${urlParams.get(
          'code'
        )}&role=${urlParams.get('role')}`
      )
      return
    }

    getProfile()
  })

  //let token = sessionStorage.getItem("jwtToken") || "";
  let token = getCookie('jwtToken')
  loginToken.update((x) => token)
  if (!token) prefetch(['IndexPage'])

  currentPage.subscribe((x) => {
    console.log('pageChange', x)
    page = x
  })

  loginToken.subscribe((x) => {
    console.log('loginTokenChange', x)
    if (x === undefined) {
      page = 'login'
      router.goto('/' + page)
    }
  })

  // The redesigned All recordings page is switched on per school with the "newRecordingsPage"
  // subscription flag; localStorage newRecordingsPage=1 turns it on in one browser for testing.
  // The flags arrive with the profile, so wait for them (at most 3 s) instead of showing the old page first.
  let flagWaitOver = false
  setTimeout(() => (flagWaitOver = true), 3000)
  // set by getProfile(); not part of the store's initial value
  $: subscriptionFlags = ($userConfig as any)?.effectiveSubscriptionFlags
  $: newRecordingsPage = subscriptionFlags?.newRecordingsPage === true || localFlag('newRecordingsPage')
  $: recordingsFlagsKnown = subscriptionFlags !== undefined || flagWaitOver
  // Audio Studio is switched on per teacher or school with the "audioStudio" flag; elsewhere its address leads home
  $: if ($flagsKnown && !$audioStudio && $router.path.startsWith('/audio-studio')) router.goto('/', true)
  // the new Admin Portal is switched on per school with the "adminPortalV2" flag; elsewhere its address leads to the classic page
  $: if ($flagsKnown && !$adminPortalV2 && $router.path.startsWith('/admin-portal')) router.goto('/organization-management', true)
</script>

<div class:leftMenuToggled={$leftMenuToggled} style="display: contents;">
  <Route>
    <Route path="/login">
      <LoginPage />
    </Route>
    <Route path="/registration">
      <LazyPage load={pages.RegistrationPage} />
    </Route>
    <Route path="/one-campus" let:meta>
      <LazyPage load={pages.OneCampusPage} props={{ dsns: meta.query.dsns, code: meta.query.code, role: meta.query.role }} />
    </Route>
    <Route path="/schoolplus" let:meta>
      <LazyPage load={pages.SchoolPlusPage} props={{ code: meta.query.code, role: meta.query.role }} />
    </Route>

    <Route path="/registration-invite" let:meta>
      <LazyPage load={pages.RegistrationFromInvitePage} props={{ email: meta.query.email, schoolName: meta.query.schoolName }} />
    </Route>

    <Route path="/reset-password">
      <LazyPage load={pages.ResetPasswordPage} />
    </Route>
    <Route path="/reset-password-verify/:jwtToken" let:meta>
      <LazyPage load={pages.ResetPasswordVerifyPage} props={{ jwtToken: meta.params.jwtToken }} />
    </Route>
    <Route path="/login" fallback>
      <LoginPage />
    </Route>

    {#if $loginToken}
      <Route path="/">
        <LazyPage load={pages.IndexPage} />
      </Route>
      <Route path="/index">
        <LazyPage load={pages.IndexPage} />
      </Route>

      <Route path="/marketplace-callback/:contentPackId" let:meta>
        <LazyPage load={pages.ContentMarketPlaceCallbackPage} props={{ contentPackId: meta.params.contentPackId }} />
      </Route>

      <Route path="/profile">
        <LazyPage load={pages.ProfilePage} />
      </Route>
      <Route path="/students">
        <LazyPage load={pages.StudentPage} />
      </Route>
      <Route path="/student-detail/:studentName" let:meta>
        <LazyPage load={pages.StudentDetailPage} props={{ studentName: meta.params.studentName }} />
      </Route>
      <Route path="/student-account-detail/:studentAccountId" let:meta>
        <LazyPage load={pages.StudentAccountDetailPage} props={{ studentAccountId: meta.params.studentAccountId, activeTab: "SAOverview" }} />
      </Route>
      <Route path="/student-account-detail/:studentAccountId/:activeTab" let:meta>
        <LazyPage load={pages.StudentAccountDetailPage} props={{ studentAccountId: meta.params.studentAccountId, activeTab: meta.params.activeTab }} />
      </Route>
      <Route path="/student-analytics/:studentAccountId" let:meta>
        <LazyPage load={pages.StudentAnalyticsPage} props={{ studentAccountId: meta.params.studentAccountId }} />
      </Route>
      <Route path="/students-recording">
        {#if newRecordingsPage}
          <LazyPage load={pages.AllRecordingsPage} />
        {:else if recordingsFlagsKnown}
          <LazyPage load={pages.StudentRecordingPage} />
        {/if}
      </Route>
      <Route path="/students-recording/summary">
        {#if newRecordingsPage}
          <LazyPage load={pages.AllRecordingsSummaryPage} />
        {:else if recordingsFlagsKnown}
          <LazyPage load={pages.StudentRecordingPage} />
        {/if}
      </Route>
      <Route path="/students-recording-classic">
        <LazyPage load={pages.StudentRecordingPage} />
      </Route>
      <Route path="/audio-studio">
        {#if $audioStudio}
          <LazyPage load={pages.AudioStudioPage} />
        {/if}
      </Route>
      <Route path="/audio-studio/:audioItemId" let:meta>
        {#if $audioStudio}
          <LazyPage load={pages.AudioStudioPage} props={{ audioItemId: meta.params.audioItemId }} />
        {/if}
      </Route>

      <Route path="/courses">
        <LazyPage load={pages.CoursePage} />
      </Route>
      <Route path="/courses-detail/:courseId" let:meta>
        <LazyPage load={pages.CourseDetailPage} props={{ courseId: meta.params.courseId, tab: 'lessons' }} />
      </Route>
      <Route path="/courses-detail/:courseId/:tab" let:meta>
        <LazyPage load={pages.CourseDetailPage} props={{ courseId: meta.params.courseId, tab: meta.params.tab }} />
      </Route>
      <Route path="/courses-edit">
        <LazyPage load={pages.CourseDetailEditPage} />
      </Route>
      <Route path="/courses-edit/:courseId/*" let:meta>
        <LazyPage load={pages.CourseDetailEditPage} props={{ courseId: meta.params.courseId }} />
      </Route>

      <Route path="/courses/:courseId/lesson-detail/:lessonId" let:meta>
        <LazyPage load={pages.LessonDetailPage} props={{ courseId: meta.params.courseId, lessonId: meta.params.lessonId, tab: 'exercises' }} />
      </Route>
      <Route path="/courses/:courseId/lesson-detail/:lessonId/:tab" let:meta>
        <LazyPage load={pages.LessonDetailPage} props={{ courseId: meta.params.courseId, lessonId: meta.params.lessonId, tab: meta.params.tab }} />
      </Route>

      <Route path="/courses/:courseId/lesson-edit" let:meta>
        <LazyPage load={pages.LessonDetailEditPage} props={{ courseId: meta.params.courseId }} />
      </Route>
      <Route path="/courses/:courseId/lesson-edit/:lessonId" let:meta>
        <LazyPage load={pages.LessonDetailEditPage} props={{ courseId: meta.params.courseId, lessonId: meta.params.lessonId }} />
      </Route>

      <Route
        path="/courses/:courseId/lessons/:lessonId/exercise-detail/:exerciseId"
        let:meta
      >
        <LazyPage load={pages.ExerciseDetailPage} props={{ courseId: meta.params.courseId, lessonId: meta.params.lessonId, exerciseId: meta.params.exerciseId }} />
      </Route>

      <Route
        path="/courses/:courseId/lessons/:lessonId/exercise-detail/:exerciseId/:selectedTab"
        let:meta
      >
        <LazyPage load={pages.ExerciseDetailPage} props={{ courseId: meta.params.courseId, lessonId: meta.params.lessonId, exerciseId: meta.params.exerciseId, selectedTab: meta.params.selectedTab }} />
      </Route>

      <Route path="/courses/:courseId/lessons/:lessonId/exercise-edit" let:meta>
        <LazyPage load={pages.ExerciseDetailEditPage} props={{ courseId: meta.params.courseId, lessonId: meta.params.lessonId }} />
      </Route>
      <Route
        path="/courses/:courseId/lessons/:lessonId/exercise-edit/:exerciseId"
        let:meta
      >
        <LazyPage load={pages.ExerciseDetailEditPage} props={{ courseId: meta.params.courseId, lessonId: meta.params.lessonId, exerciseId: meta.params.exerciseId }} />
      </Route>

      <Route path="/recording-detail/:recordingId" let:meta>
        <LazyPage load={pages.RecordingDetailPage} props={{ recordingId: meta.params.recordingId }} />
      </Route>
      <Route path="/recording-detail">
        <LazyPage load={pages.RecordingDetailPage} />
      </Route>

      <Route path="/search*" let:meta>
        <LazyPage load={pages.SearchResultPage} props={{ q: meta.query.q, archived: meta.query.archived }} />
      </Route>

      <!-- Practice Packs: the API decides who may use them (the practicePacks / practicePackManagement flags) -->
      <Route path="/practice-packs" let:meta>
        <LazyPage load={pages.PracticePacksPage} props={{ view: 'catalog', classId: meta.query.classId }} />
      </Route>
      <Route path="/practice-packs/:packId" let:meta>
        <LazyPage load={pages.PracticePacksPage} props={{ view: 'detail', packId: meta.params.packId, classId: meta.query.classId }} />
      </Route>
      <Route path="/practice-packs/:packId/use" let:meta>
        <LazyPage load={pages.PracticePacksPage} props={{ view: 'use', packId: meta.params.packId, classId: meta.query.classId }} />
      </Route>
      <Route path="/practice-pack-management">
        <LazyPage load={pages.PracticePacksPage} props={{ view: 'manage' }} />
      </Route>
      <Route path="/practice-pack-management/:packId" let:meta>
        <LazyPage load={pages.PracticePacksPage} props={{ view: 'edit', packId: meta.params.packId }} />
      </Route>

      <Route path="/activity-log">
        <LazyPage load={pages.ActivityLogPage} />
      </Route>

      <Route path="/organization-management">
        <LazyPage load={pages.OrganizationManagement} />
      </Route>

      <Route path="/organization-management/:activeTab" let:meta>
        <LazyPage load={pages.OrganizationManagement} props={{ activeTab: meta.params.activeTab }} />
      </Route>

      <Route path="/admin-portal">
        {#if $adminPortalV2}
          <LazyPage load={pages.AdminPortalPage} />
        {/if}
      </Route>

      <Route path="/admin-portal/:tab" let:meta>
        {#if $adminPortalV2}
          <LazyPage load={pages.AdminPortalPage} props={{ tab: meta.params.tab, query: meta.query }} />
        {/if}
      </Route>

      <Route path="/teacher-account/:teacherUserId" let:meta>
        <LazyPage load={pages.TeacherDetailPage} props={{ teacherUserId: meta.params.teacherUserId }} />
      </Route>

      <Route path="/student-roster">
        <LazyPage load={pages.StudentRosterPage} />
      </Route>

      <Route path="/onecampus-sync-wizard">
        <LazyPage load={pages.OneCampusSyncWizardPage} />
      </Route>

      <Route path="/student-roster/:studentRosterId" let:meta>
        <LazyPage load={pages.StudentRosterDetailPage} props={{ studentRosterId: meta.params.studentRosterId }} />
      </Route>

      <Route path="/student-roster/:studentRosterId/edit" let:meta>
        <LazyPage load={pages.StudentRosterEditPage} props={{ studentRosterId: meta.params.studentRosterId }} />
      </Route>

      <Route path="/students-in-org">
        <LazyPage load={pages.StudentsInOrgPage} />
      </Route>

      <Route path="/import-students-in-org">
        <LazyPage load={pages.ImportStudentsToOrgPage} />
      </Route>

      <Route path="/request-quote">
        <LazyPage load={pages.RequestQuotePage} />
      </Route>

      <Route path="/special-events">
        <LazyPage load={pages.SpecialEventPage} props={{ view: 'list' }} />
      </Route>
      <Route path="/special-events-detail/:eventId" let:meta>
        <LazyPage load={pages.SpecialEventPage} props={{ view: 'detail', eventId: meta.params.eventId }} />
      </Route>
      <Route path="/special-events-detail/:eventId/:tab" let:meta>
        <LazyPage load={pages.SpecialEventPage} props={{ view: 'detail', eventId: meta.params.eventId, tab: meta.params.tab }} />
      </Route>
      <Route path="/special-events-earlier-start/:eventId" let:meta>
        <LazyPage load={pages.SpecialEventPage} props={{ view: 'earlier-start', eventId: meta.params.eventId }} />
      </Route>
      <Route path="/special-events-student/:eventId/:studentAccountId" let:meta>
        <LazyPage load={pages.SpecialEventPage} props={{ view: 'student', eventId: meta.params.eventId, studentAccountId: meta.params.studentAccountId }} />
      </Route>

      
    {/if}
  </Route>
</div>
